import { Bob } from "../stores/Bob";
import { formatFiat, formatSatoshis } from "./formatNumbers";
import { formatDistanceToNow } from "date-fns";
import { makeTransactionsListLineForOurInvoice } from "./invoices";
import { makeTransactionsListLineForOnchainInbound } from "./onchainInboundTransactions";
import { makeTransactionsListLineForOnchainOutbound } from "./onchainOutboundTransactions";
import { makeTransactionsListLineForStablePayment } from "./stablePayments";
import { makeTransactionsListLineForPlatformCredit } from "./platformCredits";

export type TransactionFilterType =
  | "all"
  | "received"
  | "sent"
  | "over10sats"
  | "over100sats"
  | "over1000sats"
  | "over10000sats";

export const TRANSACTION_FILTER_OPTIONS = [
  { value: "all", label: "All transactions" },
  { value: "received", label: "Received" },
  { value: "sent", label: "Sent" },
  { value: "over10sats", label: "10+ sats" },
  { value: "over100sats", label: "100+ sats" },
  { value: "over1000sats", label: "1,000+ sats" },
  { value: "over10000sats", label: "10,000+ sats" },
] as const;

export type TransactionListOutput = {
  label: string;
  satoshisString: string;
  isCredit: boolean;
  isConfirmed: boolean;
  fiatString: string;
  internalUuid: string;
  internalModelName: TransactionModelName;
  internalCreatedAtInteger: number;
  createdAtString: string;
  comment: string | null;
  lightningAddress?: string | null;
  zapName?: string | null;
  zapPictureUrl?: string | null;
  // Onchain-specific fields
  onchainTransactionId?: string | null;
  onchainReceivingAddress?: string | null;
  onchainConfirmations?: number | null;
};
export type TransactionModelName =
  | "OurInvoice"
  | "PlatformCredit"
  | "PayAttempt"
  | "OnchainInboundTransaction"
  | "OnchainOutboundTransaction"
  | "StablePayment";

export const makeTransactionsListLine = ({
  amountSatoshis,
  label,
  createdAtInteger,
  comment,
  currencySnapshot,
  currency,
  isCredit,
  isConfirmed,
  modelName,
  uuid,
  lightningAddress,
  zapName,
  zapPictureUrl,
  onchainTransactionId,
  onchainReceivingAddress,
  onchainConfirmations,
}: {
  amountSatoshis: number;
  label: string;
  createdAtInteger: number;
  comment: string | null;
  currencySnapshot: any; // Replace 'any' with the actual type if known
  currency: string;
  isCredit: boolean;
  isConfirmed: boolean;
  modelName: TransactionModelName;
  uuid: string;
  lightningAddress?: string | null;
  zapName?: string | null;
  zapPictureUrl?: string | null;
  onchainTransactionId?: string | null;
  onchainReceivingAddress?: string | null;
  onchainConfirmations?: number | null;
}): TransactionListOutput => {
  return {
    label: label,
    satoshisString: formatSatoshis(amountSatoshis, isCredit, "sat"),
    isCredit,
    isConfirmed,
    fiatString: formatFiat({
      satoshis: amountSatoshis,
      currency,
      currencySnapshot,
      isNegative: !isCredit,
    }),
    internalUuid: uuid,
    internalModelName: modelName,
    internalCreatedAtInteger: createdAtInteger,
    createdAtString: formatDistanceToNow(new Date(createdAtInteger), {
      addSuffix: true,
    }),
    comment: comment,
    lightningAddress: lightningAddress || null,
    zapName: zapName || null,
    zapPictureUrl: zapPictureUrl || null,
    onchainTransactionId: onchainTransactionId || null,
    onchainReceivingAddress: onchainReceivingAddress || null,
    onchainConfirmations: onchainConfirmations ?? null,
  };
};

// Helper function to check if a transaction meets the amount threshold
const meetsAmountThreshold = (
  amountSatoshis: number,
  threshold: number,
): boolean => {
  return Math.abs(amountSatoshis) >= threshold;
};

// Helper function to apply filters to invoices
const filterInvoices = (
  invoices: any[],
  filterType: TransactionFilterType,
): any[] => {
  switch (filterType) {
    case "received":
      return invoices; // All invoices are received payments
    case "sent":
      return []; // Invoices are received, not sent
    case "over10sats":
    case "over100sats":
    case "over1000sats":
    case "over10000sats":
      // Amount-based filters should include invoices regardless of direction
      const threshold =
        filterType === "over10sats"
          ? 10
          : filterType === "over100sats"
            ? 100
            : filterType === "over1000sats"
              ? 1000
              : 10000;
      return invoices.filter((invoice) =>
        meetsAmountThreshold(invoice.amount_satoshis || 0, threshold),
      );
    default:
      return invoices;
  }
};

// Helper function to apply filters to platform adjustments
const filterPlatformCredits = (
  credits: any[],
  filterType: TransactionFilterType,
): any[] => {
  switch (filterType) {
    case "received":
      return credits.filter((credit) => credit.amount_in_satoshis >= 0);
    case "sent":
      return credits.filter((credit) => credit.amount_in_satoshis < 0);
    case "over10sats":
    case "over100sats":
    case "over1000sats":
    case "over10000sats":
      // Amount-based filters should include adjustments regardless of direction
      const threshold =
        filterType === "over10sats"
          ? 10
          : filterType === "over100sats"
            ? 100
            : filterType === "over1000sats"
              ? 1000
              : 10000;
      return credits.filter((credit) =>
        meetsAmountThreshold(credit.amount_in_satoshis || 0, threshold),
      );
    default:
      return credits;
  }
};

// Helper function to apply filters to pay attempts
const filterPayAttempts = (
  payAttempts: any[],
  filterType: TransactionFilterType,
): any[] => {
  switch (filterType) {
    case "received":
      return []; // Pay attempts are sent, not received
    case "sent":
      return payAttempts; // Pay attempts are sent payments
    case "over10sats":
    case "over100sats":
    case "over1000sats":
    case "over10000sats":
      // Amount-based filters should include pay attempts regardless of direction
      const threshold =
        filterType === "over10sats"
          ? 10
          : filterType === "over100sats"
            ? 100
            : filterType === "over1000sats"
              ? 1000
              : 10000;
      return payAttempts.filter((pa) => {
        // Extract amount from the transactions list line - need to parse the satoshis string
        const satoshisStr = pa.transactionsListLine.satoshisString;
        const amount = parseInt(satoshisStr.replace(/[^\d]/g, "")) || 0;
        return meetsAmountThreshold(amount, threshold);
      });
    default:
      return payAttempts;
  }
};

export const makeTransactionListsLines = (
  bob: Bob,
  filterType: TransactionFilterType = "all",
): TransactionListOutput[] => {
  const lines: TransactionListOutput[] = [];

  // For amount-based filters, we need to collect ALL transactions first,
  // then filter by amount regardless of type
  const isAmountFilter = [
    "over10sats",
    "over100sats",
    "over1000sats",
    "over10000sats",
  ].includes(filterType);

  if (isAmountFilter) {
    // Collect all transactions without direction filtering
    bob.ourInvoices.data.forEach((invoice) => {
      lines.push(makeTransactionsListLineForOurInvoice(invoice, bob));
    });

    bob.platformCredits.data.forEach((credit) => {
      lines.push(makeTransactionsListLineForPlatformCredit(credit, bob));
    });

    bob.payAttemptsPersisted.forEach((payAttempt) => {
      lines.push(payAttempt.transactionsListLine);
    });

    bob.ourOnchainInboundTransactions.data.forEach((tx) => {
      lines.push(makeTransactionsListLineForOnchainInbound(tx, bob));
    });

    bob.ourOnchainOutboundTransactions.data.forEach((tx) => {
      lines.push(makeTransactionsListLineForOnchainOutbound(tx, bob));
    });

    bob.ourStablePayments.data.forEach((tx) => {
      lines.push(makeTransactionsListLineForStablePayment(tx, bob));
    });

    // Filter by amount threshold
    const threshold =
      filterType === "over10sats"
        ? 10
        : filterType === "over100sats"
          ? 100
          : filterType === "over1000sats"
            ? 1000
            : 10000;

    const filteredLines = lines.filter((line) => {
      // Extract amount from satoshisString - remove all non-digits
      const amount = parseInt(line.satoshisString.replace(/[^\d]/g, "")) || 0;
      return amount >= threshold;
    });

    // Sort by creation time (newest first)
    return filteredLines.sort(
      (a, b) => b.internalCreatedAtInteger - a.internalCreatedAtInteger,
    );
  } else {
    // For directional filters (all, received, sent), use the existing logic
    const filteredInvoices = filterInvoices(bob.ourInvoices.data, filterType);
    filteredInvoices.forEach((invoice) => {
      lines.push(makeTransactionsListLineForOurInvoice(invoice, bob));
    });

    const filteredCredits = filterPlatformCredits(
      bob.platformCredits.data,
      filterType,
    );
    filteredCredits.forEach((credit) => {
      lines.push(makeTransactionsListLineForPlatformCredit(credit, bob));
    });

    const filteredPayAttempts = filterPayAttempts(
      bob.payAttemptsPersisted,
      filterType,
    );
    filteredPayAttempts.forEach((payAttempt) => {
      lines.push(payAttempt.transactionsListLine);
    });

    // Add onchain inbound transactions (they are always received)
    if (filterType === "all" || filterType === "received") {
      bob.ourOnchainInboundTransactions.data.forEach((tx) => {
        lines.push(makeTransactionsListLineForOnchainInbound(tx, bob));
      });
    }

    // Add onchain outbound transactions (they are always sent)
    if (filterType === "all" || filterType === "sent") {
      bob.ourOnchainOutboundTransactions.data.forEach((tx) => {
        lines.push(makeTransactionsListLineForOnchainOutbound(tx, bob));
      });
    }

    // Add stable payments (they are always sent)
    if (filterType === "all" || filterType === "sent") {
      bob.ourStablePayments.data.forEach((tx) => {
        lines.push(makeTransactionsListLineForStablePayment(tx, bob));
      });
    }

    // Sort by creation time (newest first)
    return lines.sort(
      (a, b) => b.internalCreatedAtInteger - a.internalCreatedAtInteger,
    );
  }
};
